/**
 * The audit-log filter bar.
 *
 * Four filters narrow what the SERVER returns -- date range, actor, event
 * family, outcome -- and two shape what the reader SEES: the clock every
 * timestamp renders in, and a contains-search across the rows already loaded.
 * The two kinds sit together because the reader does not care which side of
 * the wire a filter runs on; the search box says where it applies, which is
 * the only part that could mislead.
 *
 * The entity type + entity id pair that used to end this row is GONE. It
 * demanded an internal type name and a full UUID before it narrowed anything,
 * and the search box now reaches both columns by typing.
 *
 * The controls STRETCH to fill the row rather than clustering at the left
 * above a table that spans the full width. The search box is given the largest
 * share of the slack, because it is the only one whose content is unbounded.
 *
 * The actor control offers one or more actors, listed from the LOG for the
 * date range on screen rather than from a user directory. A directory could
 * not answer here: it holds no background job and no failed sign-in whose
 * presented identity matched nobody, and those are the actors an investigation
 * is usually chasing.
 */

import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

import type { AuditActor, AuditEventFamily, AuditOutcome } from '@/api/client';

import { ActorPicker } from './ActorPicker';
import { DateRangeField } from './DateRangeField';
import { toggleActor, type AuditLogFilterState } from './auditFilters';
import type { TimeZoneMode } from './auditTime';

interface Props {
  filters: AuditLogFilterState;
  problem: string | null;
  canReset: boolean;
  /** The actors the log holds in the chosen range, from the server. */
  actorOptions: readonly AuditActor[];
  actorsLoading: boolean;
  /** How many loaded rows the search currently leaves, for the count beside it. */
  matchCount: number;
  loadedCount: number;
  onChange: (next: AuditLogFilterState) => void;
  onReset: () => void;
}

/**
 * The search input, debounced.
 *
 * It filters rows the browser already holds, so it can update as the reader
 * types -- but re-filtering on every keystroke over a few thousand loaded rows
 * makes the caret stutter, and a short settle costs nothing perceptible.
 */
function useDebouncedText(value: string, commit: (next: string) => void, delay = 150) {
  const [draft, setDraft] = useState(value);

  /* `commit` closes over the WHOLE filter state, and is rebuilt every render.
     Held in a ref and read when the timer fires, so a pending keystroke
     commits against the newest state -- typing and then changing the outcome
     within the debounce window must not let the text land on top of a filter
     object that predates the outcome change. A plain dependency would instead
     restart the timer on every unrelated re-render, and never settle. */
  const latest = useRef(commit);
  useEffect(() => {
    latest.current = commit;
  });

  // The parent clearing the filters must clear the box too.
  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => latest.current(draft), delay);
    return () => window.clearTimeout(timer);
  }, [draft, value, delay]);

  return [draft, setDraft] as const;
}

export function AuditFilterBar({
  filters,
  problem,
  canReset,
  actorOptions,
  actorsLoading,
  matchCount,
  loadedCount,
  onChange,
  onReset,
}: Props) {
  const set = (patch: Partial<AuditLogFilterState>) => onChange({ ...filters, ...patch });
  const [search, setSearch] = useDebouncedText(filters.text, (next) => set({ text: next }));

  const searching = filters.text.trim() !== '';

  return (
    <div className="audit-filters">
      <div className="toolbar audit-toolbar">
        <DateRangeField
          fromDay={filters.fromDay}
          toDay={filters.toDay}
          onChange={(range) => set(range)}
        />

        <ActorPicker
          chosen={filters.actors}
          options={actorOptions}
          loading={actorsLoading}
          onToggle={(actor) => set({ actors: toggleActor(filters.actors, actor) })}
          onClear={() => set({ actors: [] })}
        />

        <select
          aria-label="Event family"
          className="filter-select"
          value={filters.eventFamily}
          onChange={(event) => set({ eventFamily: event.target.value as AuditEventFamily | '' })}
        >
          <option value="">All event families</option>
          <option value="DATA_CHANGE">Data change</option>
          <option value="AUTHENTICATION">Authentication</option>
        </select>

        <select
          aria-label="Outcome"
          className="filter-select"
          value={filters.outcome}
          onChange={(event) => set({ outcome: event.target.value as AuditOutcome | '' })}
        >
          <option value="">All outcomes</option>
          <option value="SUCCESS">Success</option>
          <option value="DENIED">Denied</option>
          <option value="FAILED">Failed</option>
        </select>

        {/* Two clocks, both always visible: a reader mid-investigation needs to
            see which one they are in without opening anything. */}
        <div className="zone-toggle" role="group" aria-label="Timestamp time zone">
          {(['local', 'utc'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              className={filters.timeZone === mode ? 'zone-option is-chosen' : 'zone-option'}
              aria-pressed={filters.timeZone === mode}
              title={
                mode === 'utc'
                  ? 'Show every timestamp in UTC, the clock entries are stored in'
                  : 'Show every timestamp in your own time zone'
              }
              onClick={() => set({ timeZone: mode as TimeZoneMode })}
            >
              {mode === 'utc' ? 'UTC' : 'Local'}
            </button>
          ))}
        </div>

        <div className="search-field audit-search">
          <Search aria-hidden="true" />
          <input
            type="search"
            aria-label="Search loaded entries"
            placeholder="Search every column…"
            spellCheck={false}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {canReset && (
          <button type="button" className="btn btn-ghost btn-sm audit-clear" onClick={onReset}>
            <X aria-hidden="true" /> Clear filters
          </button>
        )}
      </div>

      {/* Said out loud, because it is the one thing about this box that could
          mislead: it narrows what has been LOADED, not the whole log. */}
      {searching && (
        <div className="hint audit-search-note">
          {matchCount} of {loadedCount} loaded {loadedCount === 1 ? 'entry' : 'entries'} match
          {' “'}
          {filters.text.trim()}
          {'”'}. Load more entries to search further back.
        </div>
      )}

      {problem && (
        <div className="field-error" role="alert">
          {problem}
        </div>
      )}
    </div>
  );
}
